import Callout from "components/callout/callout"
import { Link } from "react-router-dom"
import { useTranslation } from "react-i18next"
import AmountTag from "components/tags/amountTag"
import Tag from "components/tags/tag"
import useProducts from "hooks/useProducts"
import useStorages from "hooks/useStorages"
import LoadMore from "components/list/loadMore"
import Section from "components/layout/section"
import { ReactComponent as AddIcon } from "assets/icons/add.svg"
import useCategories from "hooks/useCategories"
import useLocations from "hooks/useLocations"
import IconImage from "components/image/IconImage"
import { ReactComponent as DescriptionIcon } from "assets/icons/description.svg"
import DocList, { ViewMode } from "components/list/docList/docList"
import Doc from "components/list/docList/doc"
import TableListRow from "components/list/tableList/tableListRow"
import TableListCell from "components/list/tableList/tableListCell"
import TableList from "components/list/tableList/tableList"
import Button from "components/inputs/button"
import useAccess from "@/hooks/useAccess"
import { AccessRole, DeviceSize, FormStatus } from "@/types"
import Image from "@/components/image/Image"
import { ReactComponent as ImageIcon } from "assets/icons/image.svg"
import ProductFilter from "./productFilter"
import useWindowDimensions from "@/hooks/useWindowDimensions"

interface ProductListProps {
    storageId: string
    categoryId?: string
    locationId?: string
    displayHelper?: boolean,
    enableActions?: boolean
}

const ProductList: React.FC<ProductListProps> = ({ storageId, categoryId, locationId, displayHelper = true, enableActions = true }) => {

    const { t } = useTranslation()
    const { deviceSize } = useWindowDimensions()

    const { storage } = useStorages({ storageId })

    const {
        productsStatus,
        hasNextPage,
        fetchNextPage,
        filterStockStatus,
        setFilterStockStatus,
        filterDraftStatus,
        setFilterDraftStatus,
        filteredProducts,
        filterQuery,
        setFilterQuery,
        filterCategory,
        setFilterCategory,
        sortBy,
        setSortBy,
        sortDirection,
        setSortDirection,
        displayDeleted,
        setDisplayDeleted,
        queryMatchingAttributes
    } = useProducts({ storageId, categoryId, locationId })

    const { categories, getCategoryNamesString, findCategory } = useCategories({ storageId })
    const { getLocationNamesString } = useLocations({ storageId })

    const {
        limitToRoles,
        userInvitationsStatus
    } = useAccess({ storageId })

    // Helper function to render custom fields for a product
    const renderCustomFields = (product: any) => {
        const customFieldsElements: React.ReactElement[] = []
        
        Object.entries(queryMatchingAttributes).forEach(([categoryId, customInputNames]) => {
            if (categoryId === "_" || !product.customInputs) return
            
            const customInputCategory = product.customInputs[categoryId]
            
            if (customInputNames.length > 0) {
                customInputNames.forEach((customInputName, index) => {
                    const value = customInputCategory ? customInputCategory[customInputName] : "-"
                    customFieldsElements.push(
                        <span key={`${categoryId}-${customInputName}-${index}`} className="text-sm text-gray-600">
                            <strong>{customInputName}:</strong> {value}
                        </span>
                    )
                })
            }
        })
        
        return customFieldsElements
    }

    // Handle errors.
    if (storageId === undefined) return <Callout type={`error`}>{t('noStorageId')}</Callout>
    if (
        !storage?.counters?.productCounter &&
        displayHelper &&
        limitToRoles([AccessRole.EDITOR, AccessRole.OWNER])
    ) {
        return (
            <Section design={`card-centered`}>
                <IconImage>
                    <DescriptionIcon />
                </IconImage>
                <h2>{t(`titleNoProducts`)}</h2>
                <p>{t('productListNoProducts')}</p>
                <Link
                    className="block w-full md:inline-block md:w-fit"
                    to={`/storage/products/create`}
                    state={{
                        new: true,
                        categoryId,
                        locationId,
                    }}
                ><Button wideOnMobile design="primary"><AddIcon />{t('buttonCreateProduct')}</Button></Link>
            </Section>
        )
    }

    // Render the JSX.
    return (
        <>
            <DocList
                filterElement={
                    <ProductFilter
                        filterStockStatus={filterStockStatus} 
                        setFilterStockStatus={setFilterStockStatus}
                        filterDraftStatus={filterDraftStatus}
                        setFilterDraftStatus={setFilterDraftStatus}
                        filterQuery={filterQuery}
                        setFilterQuery={setFilterQuery}
                        filterCategory={filterCategory}
                        setFilterCategory={categoryId ? undefined : setFilterCategory}
                        categories={categories}
                        isLoading={productsStatus === FormStatus.LOADING || userInvitationsStatus === FormStatus.LOADING}
                        allowedSortBy={["sku","name","amount","location"]}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        sortDirection={sortDirection}
                        setSortDirection={setSortDirection}
                        displayDeleted={displayDeleted}
                        setDisplayDeleted={setDisplayDeleted}
                    />
                }
                noResultsElement={
                    <Callout type={`error`} className={`mx-2`}>{t(`labelNoResults`)}</Callout>
                }
                showResults={(filteredProducts.length > 0) || productsStatus !== FormStatus.SUCCESS}
                id={`products-list-filter`}
                viewModes={deviceSize == DeviceSize.MD || deviceSize == DeviceSize.LG ? [ViewMode.List, ViewMode.Table] : [ViewMode.List]}
                defaultViewMode={ViewMode.List}
            >
                <div data-view-mode={ViewMode.Table}>
                    <TableList>
                        <TableListRow isHeader={true}>
                            <TableListCell></TableListCell>
                            <TableListCell>{t(`labelSKU`)}</TableListCell>
                            <TableListCell>{t(`columnName`)}</TableListCell>
                            <TableListCell></TableListCell>
                            <TableListCell>{t(`columnLocation`)}</TableListCell>
                            <TableListCell>{t(`columnCategories`)}</TableListCell>
                            {Object.entries(queryMatchingAttributes).map(([categoryId, customInputNames], _key) => {
                                if (categoryId === "_") return null
                                const categoryName = findCategory(categoryId)?.name
                                if (customInputNames.length > 0) {
                                    return customInputNames.map((customInputName, key) => (
                                        <TableListCell key={key} className="capitalize" direction="vertical">
                                            <span className={`text-gray-500`}>{categoryName}</span><span>{customInputName}</span>
                                        </TableListCell>
                                    ))
                                }
                                return null
                            })}
                        </TableListRow>
                        {filteredProducts.map((product, key) => {
                            if (!product.id) return null
                            return (
                                <TableListRow
                                    key={key}
                                    to={`/storage/products/${product.id}`}
                                    isDeleted={!!product.isDeleted}
                                >
                                    <TableListCell>
                                        {product.images && product.images.length > 0 ? (
                                            <Image 
                                                src={product.images[0].url} 
                                                alt={product.name}
                                                size="sm"
                                            />
                                        ) : <Image size="sm"/> }
                                    </TableListCell>
                                    <TableListCell>{product.sku}</TableListCell>
                                    <TableListCell>
                                        <div className="flex items-center gap-2">
                                            <span>{product.name}</span>
                                            {product.isDraft && <Tag colorScheme="yellow">{t('tagDraft')}</Tag>}
                                        </div>
                                    </TableListCell>
                                    <TableListCell>
                                        <AmountTag amount={Number(product.status?.currentAmount)} unit={product.unit} lowStockWarning={product.stockSettings?.lowStockWarning} />
                                    </TableListCell>
                                    <TableListCell>{getLocationNamesString(product.locations || [])}</TableListCell>
                                    <TableListCell>{getCategoryNamesString(product.categories)}</TableListCell>
                                    {Object.entries(queryMatchingAttributes).map(([categoryId, customInputNames], _key) => {
                                        if (categoryId === "_" || !product.customInputs) return null
                                        const customInputCategory = product.customInputs[categoryId]
                                        if (customInputNames.length > 0) {
                                            return customInputNames.map((customInputName, key) => {
                                                if (!customInputCategory) return <TableListCell key={key}>-</TableListCell>
                                                return <TableListCell key={key}>{customInputCategory[customInputName]}</TableListCell>
                                            })
                                        } else {
                                            return null
                                        }
                                    })}
                                </TableListRow>
                            )}
                        )}
                        { productsStatus === FormStatus.LOADING && (
                            Array.from({ length: 3 }).map((_, _index) => (
                                <TableListRow key={_index}>
                                    <TableListCell><p className="skeleton"></p></TableListCell>
                                    <TableListCell><p className="skeleton"></p></TableListCell>
                                    <TableListCell><p className="skeleton"></p></TableListCell>
                                    <TableListCell><p className="skeleton"></p></TableListCell>
                                    <TableListCell><p className="skeleton"></p></TableListCell>
                                    <TableListCell><p className="skeleton"></p></TableListCell>
                                    {/* Add skeleton cells for custom fields */}
                                    {Object.entries(queryMatchingAttributes).map(([categoryId, customInputNames], _key) => {
                                        if (categoryId === "_") return null
                                        return customInputNames.map((_, key) => (
                                            <TableListCell key={key}><p className="skeleton"></p></TableListCell>
                                        ))
                                    })}
                                </TableListRow>
                            ))
                        )}
                    </TableList>
                </div>
                <div data-view-mode={ViewMode.List} className="flex flex-col gap-4">
                    {filteredProducts.map((product, key) => {    
                        if (!product.id) return null
                        return (
                            <Doc
                                key={key}
                                title={`${product.sku}: ${product.name}`}
                                image={product.images && product.images.length > 0 ? (
                                    <Image
                                        src={product.images[0].url} 
                                        alt={product.name}
                                        size={deviceSize == DeviceSize.SM ? "sm" : "md"}
                                    />
                                ) : <Image size={deviceSize == DeviceSize.SM ? "sm" : "md"} />}
                                direction="horizontal"
                                to={`/storage/products/${product.id}`}
                                isDeleted={!!product.isDeleted}
                                rightColumn={
                                    <AmountTag amount={Number(product.status?.currentAmount)} unit={product.unit} lowStockWarning={product.stockSettings?.lowStockWarning} size="lg" />
                                }
                            >
                                {product.isDraft && (
                                    <div className="flex flex-wrap gap-2 items-center">
                                        <Tag colorScheme="yellow">{t('tagDraft')}</Tag>
                                    </div>
                                )}
                                {product.locations && product.locations.length > 0 && <div>{getLocationNamesString(product.locations)}</div>}
                                {product.categories.length > 0 && <div>{getCategoryNamesString(product.categories)}</div>}
                                <div className="flex flex-wrap gap-2">
                                    {renderCustomFields(product)}
                                </div>
                            </Doc>
                        )
                    })}
                    { productsStatus === FormStatus.LOADING &&
                        Array.from({ length: 3 }).map((_, _index) => (
                            <Doc key={_index} isLoading={true}>
                                <p className="skeleton"></p>
                                <p className="skeleton"></p>
                                <p className="skeleton"></p>
                            </Doc>
                        ))
                    }
                </div>
            </DocList>
            <LoadMore hasNextPage={hasNextPage} action={fetchNextPage} isLoading={productsStatus === FormStatus.LOADING} trigger={filteredProducts} />
        </>
    )

}

export default ProductList

